import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ImagePlus, ChevronDown, ChevronUp, X } from 'lucide-react';
import api from '../api';
import socket from '../socket';
import LinkPreview from '../components/LinkPreview';
import AdPlaceholder from '../components/AdPlaceholder';
import VerdictBar, { getPostVerdict } from '../components/VerdictBar';
import ShareButton from '../components/ShareButton';

const STATUS_LEFT_BORDER = { green: 'var(--color-verified)', red: 'var(--color-invalidated)', neutral: 'var(--color-text-muted)' };

function stringToColor(str) {
  if (!str) return 'hsl(0, 0%, 60%)';
  let hash = 0;
  for (let i = 0; i < str.length; i++) { hash = str.charCodeAt(i) + ((hash << 5) - hash); }
  return `hsl(${Math.abs(hash) % 360}, 60%, 42%)`;
}

function Avatar({ username, size = 28 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: stringToColor(username),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 600, color: '#ffffff', flexShrink: 0,
      fontFamily: 'var(--font-body)',
    }}>
      {username?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

function formatPostDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffHrs = diffMs / 3600000;
  if (diffHrs < 1) {
    const mins = Math.floor(diffMs / 60000);
    return mins <= 1 ? 'just now' : `${mins}m ago`;
  }
  if (diffHrs < 24) return `${Math.floor(diffHrs)}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}


const _regionNames = (() => { try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; } })();

const FlagIcon = ({ countryCode }) => {
  if (!countryCode) return null;
  const code = countryCode.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/16x12/${code}.png`}
      alt={countryCode}
      title={getCountryTitle(countryCode)}
      style={{
        width: 16,
        height: 12,
        marginLeft: 4,
        verticalAlign: 'middle',
        borderRadius: 2,
      }}
    />
  );
};

const getCountryTitle = (countryCode) => {
  if (!countryCode) return '';
  try { return _regionNames?.of(countryCode.toUpperCase()) || countryCode.toUpperCase(); } catch { return countryCode.toUpperCase(); }
};

function getAIBadge(score) {
  if (!score || score < 0.40) return null;
  const pct = Math.round(score * 100);
  if (score < 0.70) return { label: `${pct}% AI probability`,     color: '#92400e', bg: '#fef3c7' };
  if (score < 0.90) return { label: `${pct}% likely AI-generated`, color: '#c2410c', bg: '#ffedd5' };
  return                    { label: `${pct}% AI-generated`,        color: '#ffffff', bg: '#dc2626' };
}

// Module-level unsaved-text tracker — any reply box with content sets this
let _hasUnsavedText = false;
export function setUnsavedText(val) { _hasUnsavedText = val; }

export default function Feed({ user }) {
  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]     = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  // IDs of posts submitted in this session that are still being analyzed by the pipeline
  const [analyzingPosts, setAnalyzingPosts] = useState(new Set());
  // Mirror of posts for use inside socket callbacks (avoids stale closure)
  const postsRef = useRef([]);
  const [content, setContent]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);

  // Unsaved text warning — beforeunload
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    const onBeforeUnload = (e) => {
      if (_hasUnsavedText) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);
  // Duplicate detection state
  const [dupPostId, setDupPostId]   = useState(null);
  const [dupPending, setDupPending] = useState(false);
  const [dupPreview, setDupPreview] = useState(null);
  // Inline link detection
  const [detectedUrl, setDetectedUrl]         = useState(null);
  const [detectedUrlDupId, setDetectedUrlDupId] = useState(null);
  const [urlDupInfo, setUrlDupInfo]           = useState(null);
  // Image upload state
  const [imageFile, setImageFile]       = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading]       = useState(false);
  const fileInputRef = useRef(null);
  const dismissedUrlsRef = useRef(new Set());
  // Scroll position restoration — saved before navigating to a post, restored on mount
  const [savedScrollY, setSavedScrollY] = useState(() => {
    const y = sessionStorage.getItem('feedScrollY');
    if (y) { sessionStorage.removeItem('feedScrollY'); return parseInt(y, 10); }
    return null;
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  // Socket: patch verdict in-place for posts already in the feed;
  // show banner only for posts not currently visible (new posts from other users)
  useEffect(() => {
    const handleFeedUpdated = ({ postId, verdict, linkVerdict, claimStatuses }) => {
      console.log('[Socket] feed_updated:', postId, verdict,
        '| feed IDs:', postsRef.current.map(p => p.id).join(','));
      if (!postId || !verdict) return;
      const exists = postsRef.current.some(p => String(p.id) === String(postId));
      if (exists) {
        // Post is already visible — patch its verdict without re-fetching
        console.log('[SOCKET] feed_updated — patching post', postId, 'verdict:', verdict);
        setPosts(prev => prev.map(post =>
          String(post.id) === String(postId)
            ? {
                ...post,
                verdict,
                ...(linkVerdict  && { link_verdict: linkVerdict }),
                ...(claimStatuses?.length && { claim_statuses: claimStatuses }),
              }
            : post
        ));
        // Keep postsRef in sync
        postsRef.current = postsRef.current.map(p =>
          String(p.id) === String(postId) ? { ...p, verdict } : p
        );
        // Once a real verdict arrives, stop showing the Analyzing badge
        if (verdict && verdict !== 'new') {
          setAnalyzingPosts(prev => {
            const next = new Set(prev);
            next.delete(String(postId));
            return next;
          });
        }
      } else {
        // New post not yet in list — ignore (no banner)
        console.log('[SOCKET] feed_updated — new post (not in list), ignoring for post', postId);
      }
    };
    socket.on('feed_updated', handleFeedUpdated);
    return () => socket.off('feed_updated', handleFeedUpdated);
  }, []);

  // Restore scroll position after posts load (back-to-feed navigation)
  useEffect(() => {
    if (!loading && savedScrollY !== null) {
      window.scrollTo(0, savedScrollY);
      setSavedScrollY(null);
    }
  }, [loading, savedScrollY]);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/posts');
      const fetched = res.data.posts || [];
      setPosts(fetched);
      postsRef.current = fetched;
      setHasMore(res.data.hasMore || false);
      setNextCursor(res.data.nextCursor || null);
      console.log('[FEED] hasMore:', res.data.hasMore, 'nextCursor:', res.data.nextCursor);
      // Clean up analyzingPosts: any post that now has a real verdict no longer needs the badge.
      // Handles the case where the socket event was missed or fired with verdict='new' (Rule 1b).
      setAnalyzingPosts(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        for (const post of fetched) {
          if (post.verdict && post.verdict !== 'new') {
            next.delete(String(post.id));
          }
        }
        return next.size === prev.size ? prev : next;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMorePosts = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get(`/posts?before=${encodeURIComponent(nextCursor)}`);
      const newPosts = res.data.posts || [];
      const appended = [...postsRef.current, ...newPosts];
      setPosts(appended);
      postsRef.current = appended;
      setHasMore(res.data.hasMore || false);
      // Derive next cursor from the oldest created_at in the newly fetched batch.
      // This is more reliable than res.data.nextCursor since it is computed directly
      // from the data we received rather than depending on the API to echo it back.
      if (newPosts.length > 0) {
        const oldest = newPosts.reduce((a, b) =>
          new Date(a.created_at) < new Date(b.created_at) ? a : b
        );
        setNextCursor(oldest.created_at);
      } else {
        setNextCursor(null);
      }
      console.log('[FEED] loadMore: fetched', newPosts.length, 'posts, hasMore:', res.data.hasMore);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const doSubmitPost = async () => {
    setSubmitting(true);
    setError('');
    setDupPending(false);
    setDupPostId(null);
    setDupPreview(null);
    setDetectedUrl(null);
    setDetectedUrlDupId(null);
    setUrlDupInfo(null);
    dismissedUrlsRef.current.clear();

    try {
      let imageData = {};

      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploading(false);
        imageData = {
          image_url:        uploadRes.data.url,
          image_public_id:  uploadRes.data.public_id,
          image_ai_score:   uploadRes.data.ai_detection?.ai_score ?? null,
          image_ai_flagged: uploadRes.data.ai_detection?.is_flagged ?? false,
        };
      }

      const newPost = await api.post('/posts', { content, ...imageData });
      const newPostId = String(newPost.data.id);
      setContent('');
      setUnsavedText(false);
      clearImage();
      setAnalyzingPosts(prev => new Set([...prev, newPostId]));
      fetchPosts();
    } catch (err) {
      setUploading(false);
      // Show 429/error message inline without clearing input — prefer `message` over raw `error`
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContentChange = (e) => {
    const val = e.target.value;
    setContent(val);
    setUnsavedText(val.length > 0);
    const match = val.match(/https?:\/\/[^\s]+/);
    const url = match ? match[0] : null;

    dismissedUrlsRef.current.forEach(dismissed => {
      if (!val.includes(dismissed)) dismissedUrlsRef.current.delete(dismissed);
    });

    if (url === detectedUrl) return;
    if (url && dismissedUrlsRef.current.has(url)) return;

    setDetectedUrl(url);
    setDetectedUrlDupId(null);
    setUrlDupInfo(null);
    if (url) {
      api.get(`/posts/check-duplicate?url=${encodeURIComponent(url)}`)
        .then(r => {
          if (r.data.duplicate) {
            setDetectedUrlDupId(r.data.post_id);
            setUrlDupInfo(r.data.preview || null);
          }
        })
        .catch(() => {});
    }
  };

  const dismissUrlPreview = () => {
    if (detectedUrl) dismissedUrlsRef.current.add(detectedUrl);
    setDetectedUrl(null);
    setDetectedUrlDupId(null);
    setUrlDupInfo(null);
    setDupPending(false);
    setDupPostId(null);
    setDupPreview(null);
  };

  const submitPost = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/auth'); return; }
    if (!content.trim() && !imageFile) {
      setError('Add a claim, article link, or image.');
      return;
    }

    if (detectedUrlDupId) {
      // Paste handler already showed inline warning — submit directly
      await doSubmitPost();
      return;
    }

    const urlMatch = content.match(/https?:\/\/[^\s]+/);
    if (urlMatch) {
      try {
        const dupRes = await api.get(`/posts/check-duplicate?url=${encodeURIComponent(urlMatch[0])}`);
        if (dupRes.data.duplicate) {
          setDupPostId(dupRes.data.post_id);
          setDupPreview(dupRes.data.preview || null);
          setDupPending(true);
          return;
        }
      } catch (_) {}
    }

    await doSubmitPost();
  };

  const charCount = content.length;
  const charOver = charCount > 3000;
  const charNearLimit = charCount > 2800 && !charOver;
  // FIX 5: disable when nothing has been entered
  const isSubmitDisabled = submitting || uploading || !user || charOver || (!content.trim() && !imageFile);
  // FIX 11: warn when content is just a bare URL with no surrounding text
  const isBareUrl = /^https?:\/\/\S+$/.test(content.trim());

  console.log('[FEED] Rendering, hasMore:', hasMore, 'posts count:', posts.length);

  return (
    <div className="feed-layout">
      <div>
      {/* Ad slot — top of page (mobile only; desktop gets sidebar) */}
      {isMobile && <AdPlaceholder slot="feed-top" />}

      <div style={card}>
        <h2 style={{
          marginBottom: 14,
          fontSize: 20,
          fontWeight: 700,
          fontFamily: 'var(--font-display)',
          color: 'var(--color-text-primary)',
        }}>
          Make a Claim
        </h2>
        {!user && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 12, fontFamily: 'var(--font-body)' }}>
            <Link to="/auth">Sign in</Link> to post a claim.
          </p>
        )}
        <form onSubmit={submitPost}>
          <textarea
            style={{ ...inputStyle, height: 100, resize: 'vertical', fontSize: Math.max(16, inputStyle?.fontSize || 14) }}
            placeholder="Make a claim or share an article... (optional if attaching an image)"
            value={content}
            onChange={handleContentChange}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (!isSubmitDisabled && (content.trim() || imageFile)) submitPost(e);
              }
            }}
          />

          {/* Character counter */}
          {charCount > 0 && (
            <div style={{ textAlign: 'right', fontSize: 11, marginTop: 2, fontFamily: 'var(--font-body)', color: charOver ? 'var(--color-invalidated)' : charNearLimit ? 'var(--color-disputed)' : 'var(--color-text-muted)' }}>
              {charCount} / 3000
            </div>
          )}

          {/* Image attachment row */}
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach image"
              style={{
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                color: 'var(--color-text-muted)',
                padding: '5px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1,
              }}
            >
              <ImagePlus size={16} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={imagePreview}
                  alt="preview"
                  style={{ height: 48, width: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--color-border)' }}
                />
                <button
                  type="button"
                  onClick={clearImage}
                  style={{
                    background: 'var(--color-border)',
                    border: 'none', borderRadius: '50%',
                    color: 'var(--color-text-primary)',
                    width: 18, height: 18, cursor: 'pointer',
                    fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={10} />
                </button>
              </div>
            )}
            {uploading && (
              <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>Uploading image...</span>
            )}
          </div>

          {/* Inline link preview */}
          {detectedUrl && !dupPending && (
            <div style={{ marginTop: 10 }}>
              {urlDupInfo && (
                <div style={{
                  marginBottom: 6, padding: '8px 12px',
                  background: 'var(--color-new-bg)',
                  border: '1px solid var(--color-new-border)',
                  borderRadius: '6px 6px 0 0',
                }}>
                  <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: 'var(--color-new)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-body)' }}>
                    Already posted
                  </p>
                  {urlDupInfo.top_arg_synopsis && (
                    <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.4, fontFamily: 'var(--font-body)' }}>
                      Top argument: {urlDupInfo.top_arg_synopsis}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {urlDupInfo.root_status && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: STATUS_LEFT_BORDER[urlDupInfo.root_status], fontFamily: 'var(--font-body)' }}>
                        ● {urlDupInfo.root_status === 'green' ? 'Standing' : urlDupInfo.root_status === 'red' ? 'Challenged' : 'Active'}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                      {urlDupInfo.thread_count} thread{urlDupInfo.thread_count !== 1 ? 's' : ''} · {urlDupInfo.participant_count} participant{urlDupInfo.participant_count !== 1 ? 's' : ''}
                    </span>
                    {urlDupInfo.poster_stance && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                        Original poster arguing {urlDupInfo.poster_stance === 'affirmative' ? 'FOR' : 'AGAINST'} this content
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <LinkPreview
                  meta={urlDupInfo ? { og_title: urlDupInfo.og_title, og_description: urlDupInfo.og_description, og_image: urlDupInfo.og_image } : null}
                  url={detectedUrl}
                />
                <button
                  type="button"
                  onClick={dismissUrlPreview}
                  title="Dismiss preview"
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    background: 'var(--color-border)',
                    border: 'none', borderRadius: '50%',
                    color: 'var(--color-text-primary)',
                    width: 22, height: 22, cursor: 'pointer',
                    fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          )}

          {/* FIX 11: bare URL warning */}
          {isBareUrl && !error && (
            <p style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, marginTop: 8, padding: '6px 10px', fontFamily: 'var(--font-body)' }}>
              Add a sentence explaining what you want to discuss — a bare link won't be analyzed without context.
            </p>
          )}

          {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 13, marginTop: 8, fontFamily: 'var(--font-body)' }}>{error}</p>}

          {/* Duplicate URL prompt */}
          {dupPending && (
            <div style={{
              marginTop: 10,
              background: 'var(--color-new-bg)',
              border: '1px solid var(--color-new-border)',
              borderRadius: 8, padding: '12px 14px',
            }}>
              <p style={{ color: 'var(--color-new)', fontSize: 13, margin: '0 0 10px', fontWeight: 600, fontFamily: 'var(--font-body)' }}>
                This URL has already been posted.
              </p>
              {dupPreview && (
                <div style={{
                  marginBottom: 10, padding: '10px 12px',
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                }}>
                  {dupPreview.og_title && (
                    <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-body)' }}>{dupPreview.og_title}</p>
                  )}
                  {dupPreview.content && (
                    <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>
                      {dupPreview.content.slice(0, 140)}{dupPreview.content.length > 140 ? '...' : ''}
                    </p>
                  )}
                  {dupPreview.first_claim && (
                    <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.4, fontStyle: 'italic', fontFamily: 'var(--font-body)' }}>
                      Key claim: {dupPreview.first_claim}
                    </p>
                  )}
                  {dupPreview.top_arg_synopsis && (
                    <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.4, fontStyle: 'italic', fontFamily: 'var(--font-body)' }}>
                      Top argument: {dupPreview.top_arg_synopsis}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {dupPreview.root_status && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: STATUS_LEFT_BORDER[dupPreview.root_status], fontFamily: 'var(--font-body)' }}>
                        ● {dupPreview.root_status === 'green' ? 'Standing' : dupPreview.root_status === 'red' ? 'Challenged' : 'Active'}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                      {dupPreview.thread_count} thread{dupPreview.thread_count !== 1 ? 's' : ''} · {dupPreview.participant_count || 0} participant{dupPreview.participant_count !== 1 ? 's' : ''}
                    </span>
                    {dupPreview.poster_stance && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                        Original poster was arguing {dupPreview.poster_stance === 'affirmative' ? 'FOR' : 'AGAINST'} this content
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => navigate(`/posts/${dupPostId}`)}
                  style={{ ...submitBtn, fontSize: 13, padding: '6px 14px' }}
                >
                  Go to this debate →
                </button>
                <button
                  type="button"
                  onClick={doSubmitPost}
                  style={{ ...submitBtn, background: 'var(--color-border-strong)', color: 'var(--color-text-primary)', fontSize: 13, padding: '6px 14px' }}
                >
                  Post anyway
                </button>
                <button
                  type="button"
                  onClick={() => { setDupPending(false); setDupPostId(null); setDupPreview(null); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', fontSize: 12, cursor: 'pointer', padding: '6px 0', fontFamily: 'var(--font-body)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!dupPending && (
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button
                type="submit"
                disabled={isSubmitDisabled}
                style={submitBtn}
                onMouseEnter={e => { if (!isSubmitDisabled) e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-brand)')}
              >
                {uploading ? 'Uploading...' : submitting ? 'Posting...' : 'Post Claim'}
              </button>
            </div>
          )}
        </form>
      </div>

      <div style={{ marginTop: 20 }}>
        {loading ? (
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', fontFamily: 'var(--font-body)' }}>Loading...</p>
        ) : posts.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', fontFamily: 'var(--font-body)' }}>No posts yet. Be the first!</p>
        ) : (
          <>
            {posts.map((post, i) => (
              <React.Fragment key={post.id}>
                <PostCard post={post} user={user} onDeleted={fetchPosts} isAnalyzing={analyzingPosts.has(String(post.id))} setPosts={setPosts} />
                {(i + 1) % 5 === 0 && <AdPlaceholder slot={`feed-inline-${i}`} />}
              </React.Fragment>
            ))}
            {hasMore && (
              <button
                onClick={loadMorePosts}
                disabled={loadingMore}
                style={{
                  display: 'block',
                  margin: '24px auto',
                  padding: '12px 24px',
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-text-muted)',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontFamily: 'var(--font-body)',
                  opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {loadingMore ? 'Loading...' : 'Load more posts'}
              </button>
            )}
          </>
        )}
      </div>
    </div>

  </div>
);
}

// Bar colors for argument status indicators in compact list
const ARG_BAR_COLOR = {
  green:   'var(--color-verified-bar)',
  red:     'var(--color-invalidated-bar)',
  neutral: 'var(--color-neutral-bar)',
};



// Collapsed single-line reply box on feed cards — with image attach
function FeedCardReplyBox({ postId, onSuccess }) {
  const [content, setContent]       = useState('');
  const [expanded, setExpanded]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState('');
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading]   = useState(false);
  const fileInputRef = useRef(null);

  // Reset unsaved-text flag on unmount
  useEffect(() => () => setUnsavedText(false), []);

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    setExpanded(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!content.trim() && !imageFile) return;
    if (content.length > 3000) return;
    setSubmitting(true);
    setError('');
    try {
      let imageData = {};
      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadRes = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploading(false);
        imageData = {
          image_url:        uploadRes.data.url,
          image_public_id:  uploadRes.data.public_id,
          image_ai_score:   uploadRes.data.ai_detection?.ai_score ?? null,
          image_ai_flagged: uploadRes.data.ai_detection?.is_flagged ?? false,
        };
      }
      await api.post(`/posts/${postId}/respond`, { content, ...imageData });
      setUnsavedText(false);
      setContent('');
      clearImage();
      setExpanded(false);
      setSuccess(true);
      if (onSuccess) onSuccess();
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setUploading(false);
      // Show 429/error inline without clearing input — prefer `message` over raw `error`
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const hasContent = content.trim().length > 0 || imageFile;
  const replyCharOver = content.length > 3000;
  const replyCharNearLimit = content.length > 2800 && !replyCharOver;

  return (
    <div style={{ marginTop: 12, position: 'relative' }} onClick={e => e.stopPropagation()}>
      <form onSubmit={handleSubmit}>
        <div style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: 6, padding: '6px 10px',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <textarea
            rows={expanded ? 3 : 1}
            data-reply-box="true"
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--color-text-primary)', fontSize: 16, resize: 'none', width: '100%',
              lineHeight: 1.5, fontFamily: 'var(--font-body)',
              height: expanded ? 66 : 24, transition: 'height 0.15s ease',
            }}
            placeholder="Add your argument..."
            value={content}
            onChange={e => { setContent(e.target.value); setUnsavedText(e.target.value.length > 0); if (e.target.value) setExpanded(true); }}
            onFocus={() => setExpanded(true)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if ((content.trim() || imageFile) && !submitting && !uploading && content.length <= 3000) handleSubmit(e);
              }
            }}
          />
          {imagePreview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <img src={imagePreview} alt="preview"
                style={{ height: 36, width: 48, objectFit: 'cover', borderRadius: 3, border: '1px solid var(--color-border)' }} />
              <button type="button" onClick={clearImage}
                style={{ background: 'var(--color-border)', border: 'none', borderRadius: '50%', color: 'var(--color-text-primary)', width: 16, height: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={9} />
              </button>
            </div>
          )}
          {/* Action bar — image button always visible so mobile users can attach without expanding first */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                title="Attach image"
                style={{ background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 4, color: 'var(--color-text-muted)', padding: '2px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <ImagePlus size={12} />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
              {uploading && <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>Uploading...</span>}
              {expanded && content.length > 0 && (
                <span style={{ fontSize: 10, fontFamily: 'var(--font-body)', color: replyCharOver ? 'var(--color-invalidated)' : replyCharNearLimit ? 'var(--color-disputed)' : 'var(--color-text-muted)' }}>
                  {content.length} / 3000
                </span>
              )}
            </div>
            {hasContent && (
              <button type="submit" disabled={submitting || uploading || replyCharOver}
                style={{
                  padding: '3px 12px', background: 'var(--color-brand)', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                  fontWeight: 600, fontFamily: 'var(--font-body)',
                }}>
                {submitting ? '...' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </form>
      {error && <p style={{ color: 'var(--color-invalidated)', fontSize: 12, margin: '4px 0 0', fontFamily: 'var(--font-body)' }}>{error}</p>}
      {success && (
        <p style={{
          color: 'var(--color-verified)', fontSize: 12, fontFamily: 'var(--font-body)', fontWeight: 600,
          position: 'absolute', margin: 0, pointerEvents: 'none',
          opacity: success ? 1 : 0, transition: 'opacity 0.3s ease',
        }}>
          ✓ Argument added to the debate
        </p>
      )}
    </div>
  );
}

function PostCard({ post, user, onDeleted, isAnalyzing, setPosts }) {
  const navigate = useNavigate();
  const [deleting, setDeleting]           = useState(false);
  const [headlineHovered, setHeadlineHovered] = useState(false);
  const [hoveredArgId, setHoveredArgId]   = useState(null);
  const [contentCollapsed, setContentCollapsed] = useState(() => {
    try { return localStorage.getItem(`feed-post-collapsed-${post.id}`) === 'true'; } catch { return false; }
  });
  // Track mousedown position to distinguish click from drag-selection
  const mouseDownXRef = useRef(null);
  const mouseDownYRef = useRef(null);

  const needsTruncation = post.content.length > 250;
  const verdict = getPostVerdict(post);
  // Posts with verdict='new' that weren't just submitted in this session show as 'neutral' (Unverified)
  const displayVerdict = (verdict === 'new' && !isAnalyzing) ? 'neutral' : verdict;
  console.log('[PostCard] id:', post.id, 'verdict:', post.verdict, 'is_new:', post.is_new, 'display:', displayVerdict);

  const isNeutralCard = displayVerdict === 'neutral' || displayVerdict === 'new';
  const cardStyle = {
    background: '#ffffff',
    borderRadius: 'var(--radius-card)',
    border: '1px solid #e2e8f0',
    ...(isNeutralCard && { borderLeft: '3px solid #cbd5e1' }),
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    marginBottom: 16,
    padding: 0,
    overflow: 'hidden',
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm('Delete this post and all its threads? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/posts/${post.id}`);
      onDeleted();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete post');
      setDeleting(false);
    }
  };

  const handleMouseDown = (e) => {
    mouseDownXRef.current = e.clientX;
    mouseDownYRef.current = e.clientY;
  };

  const navigateToPost = (e) => {
    // Ignore if user is selecting text (either via getSelection or by dragging)
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) return;
    if (mouseDownXRef.current !== null) {
      const dx = Math.abs(e.clientX - mouseDownXRef.current);
      const dy = Math.abs(e.clientY - mouseDownYRef.current);
      if (dx > 5 || dy > 5) return; // drag, not click
    }
    sessionStorage.setItem('feedScrollY', String(window.scrollY));
    navigate(`/posts/${post.id}`, { state: { backLabel: 'Back to feed' } });
  };

  const navigateToArg = (argId) => (e) => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) return;
    sessionStorage.setItem('feedScrollY', String(window.scrollY));
    navigate(`/posts/${post.id}/a/${argId}`, { state: { backLabel: 'Back to feed' } });
  };

  return (
    <div style={cardStyle}>
      <VerdictBar
        verdict={displayVerdict}
        counterArg={post.strongest_counter_synopsis}
        moreCount={post.other_counter_count || 0}
        claimStatuses={post.claim_statuses}
        predictionVerdict={post.prediction_verdict}
      />
      {/* Prediction pill — below verdict bar, distinct from definition purple */}
      {post.has_prediction_claims && (
        <div style={{
          padding: '4px 12px',
          background: '#f3e8ff',
          borderBottom: '1px solid #a855f7',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#6b21a8', fontFamily: 'var(--font-body)' }}>
            📊 Contains prediction claims
          </span>
          {post.prediction_verdict && (
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#6b21a8',
              background: '#e9d5ff', border: '1px solid #a855f7',
              borderRadius: 10, padding: '1px 8px',
              fontFamily: 'var(--font-body)',
            }}>
              {post.prediction_verdict === 'plausible' ? 'Plausible' : post.prediction_verdict === 'implausible' ? 'Implausible' : 'Contested'}
            </span>
          )}
        </div>
      )}
      <div style={{ padding: '14px 20px' }}>
        {/* Clickable headline */}
        <a
          href={`/posts/${post.id}`}
          onClick={e => e.preventDefault()}
          onMouseUp={e => { navigateToPost(e); }}
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setHeadlineHovered(true)}
          onMouseLeave={() => setHeadlineHovered(false)}
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: (needsTruncation && contentCollapsed) ? 3 : 'unset',
            overflow: (needsTruncation && contentCollapsed) ? 'hidden' : 'visible',
            color: '#1e293b',
            marginBottom: 8,
            lineHeight: 1.5,
            cursor: 'pointer',
            textDecoration: 'none',
            fontFamily: post.post_meta?.og_title ? 'var(--font-display)' : 'var(--font-body)',
            fontSize: post.post_meta?.og_title ? 'clamp(15px, 4vw, 18px)' : 'clamp(13px, 3.5vw, 15px)',
            fontWeight: post.post_meta?.og_title ? 700 : 500,
            borderBottom: headlineHovered ? '1px solid var(--color-border-strong)' : '1px solid transparent',
          }}
        >
          {post.post_meta?.og_title || post.content}
        </a>
        {needsTruncation && (
          <button
            onClick={e => {
              e.stopPropagation();
              const next = !contentCollapsed;
              setContentCollapsed(next);
              try { localStorage.setItem(`feed-post-collapsed-${post.id}`, next ? 'true' : 'false'); } catch {}
            }}
            style={{
              background: 'transparent', border: 'none', color: 'var(--color-brand)',
              cursor: 'pointer', padding: '0 0 8px', fontSize: 11,
              fontFamily: 'var(--font-body)', fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 3,
            }}
          >
            {contentCollapsed ? <><ChevronDown size={12} /> Show more</> : <><ChevronUp size={12} /> Show less</>}
          </button>
        )}

        {/* Meta row — username, flag, date, share */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Avatar username={post.username} size={22} />
            <span style={{ fontSize: 13, color: '#64748b', fontFamily: 'var(--font-body)' }}>
              {post.username}<FlagIcon countryCode={post.country_code} />
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ShareButton
              url={`${window.location.origin}/posts/${post.id}`}
              title={post.post_meta?.og_title || post.content?.slice(0, 80)}
            />
            <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'var(--font-body)' }}>
              {formatPostDate(post.created_at)}
            </span>
          </div>
        </div>

        {/* Clickable post image */}
        {post.image_url && (
          <div onClick={navigateToPost} onMouseDown={handleMouseDown} style={{ position: 'relative', marginBottom: 10, cursor: 'pointer' }}>
            <img
              src={post.image_url}
              alt=""
              style={{
                width: '100%', maxHeight: 500, objectFit: 'contain',
                borderRadius: 8, display: 'block', background: 'var(--color-bg)',
              }}
            />
            {(() => { const badge = getAIBadge(post.image_ai_score); return badge ? (
              <span style={{
                position: 'absolute', top: 8, left: 8,
                background: badge.bg, color: badge.color,
                fontSize: 11, fontWeight: 700, padding: '3px 10px',
                borderRadius: 12, fontFamily: 'var(--font-body)',
              }}>{badge.label}</span>
            ) : null; })()}
          </div>
        )}

        {post.source_url && (
          <LinkPreview meta={post.post_meta} url={post.source_url} linkVerdict={post.link_verdict} />
        )}
        <ClaimsPanel claims={post.claims} claimStatuses={post.claim_statuses} />

        {/* Analyzing badge — only shown for posts submitted in this browser session AND still verdict='new' */}
        {isAnalyzing && verdict === 'new' && (
          <div style={{
            marginTop: 10,
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 20, padding: '3px 10px',
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'var(--color-text-muted)',
              display: 'inline-block',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <span style={{
              fontSize: 11, color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)', fontWeight: 500,
            }}>
              Analyzing…
            </span>
          </div>
        )}

        {/* Compact top arguments list */}
        {post.top_arguments && post.top_arguments.length > 0 && (() => {
          const visibleArgs = post.top_arguments.filter(arg => arg.stance === 'rebuttal' || !arg.stance);
          if (visibleArgs.length === 0) return null;
          const hiddenCount = Math.max(0, (post.response_count || 0) - visibleArgs.length);
          const feedSynopsis = (arg) => {
            const text = arg.synopsis_short || arg.synopsis || '';
            if (text.length <= 80) return text;
            const truncated = text.slice(0, 77);
            const lastSpace = truncated.lastIndexOf(' ');
            return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '...';
          };
          return (
            <div style={{ marginTop: 12 }}>
              <p style={{
                fontSize: 11, fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.05em',
                margin: '0 0 8px', fontFamily: 'var(--font-body)',
              }}>
                Top Arguments
              </p>
              {visibleArgs.map((arg, i) => {
                const isRed = arg.status === 'red';
                return (
                  <div
                    key={arg.id}
                    onClick={navigateToArg(arg.id)}
                    onMouseDown={handleMouseDown}
                    onMouseEnter={() => setHoveredArgId(arg.id)}
                    onMouseLeave={() => setHoveredArgId(null)}
                    style={{
                      display: 'flex', alignItems: 'flex-start',
                      padding: '8px 4px', margin: '0 -4px',
                      borderBottom: i < visibleArgs.length - 1 ? '1px solid #f1f5f9' : 'none',
                      cursor: 'pointer',
                      background: hoveredArgId === arg.id ? '#f8fafc' : 'transparent',
                      borderRadius: 4, transition: 'background 0.1s',
                    }}
                  >
                    <div style={{
                      width: 3, alignSelf: 'stretch', minHeight: 18,
                      borderRadius: 2, marginRight: 10, flexShrink: 0,
                      background: ARG_BAR_COLOR[arg.status] || 'var(--color-neutral-bar)',
                    }} />
                    <span style={{
                      fontSize: 13, lineHeight: 1.4, fontFamily: 'var(--font-body)',
                      color: isRed ? '#9ca3af' : '#374151',
                      fontStyle: isRed ? 'italic' : 'normal',
                    }}>
                      {feedSynopsis(arg)}
                    </span>
                  </div>
                );
              })}
              {hiddenCount > 0 && (
                <button
                  onClick={e => { e.stopPropagation(); navigateToPost(e); }}
                  style={{
                    background: 'transparent', border: 'none', color: '#94a3b8',
                    fontSize: 12, cursor: 'pointer', padding: '6px 0 0',
                    fontFamily: 'var(--font-body)', display: 'block',
                  }}
                >
                  + {hiddenCount} more arguments →
                </button>
              )}
            </div>
          );
        })()}

        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          { console.log('Post data:', post.id, post.thread_count, post.response_count) }
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
            {post.thread_count ?? 0} thread{String(post.thread_count) !== '1' ? 's' : ''}
            {post.response_count > 0 && ` · ${post.response_count} argument${post.response_count !== 1 ? 's' : ''}`}
          </span>
          {user && user.id === post.user_id && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                background: 'transparent', border: 'none', color: 'var(--color-invalidated)',
                fontSize: 12, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-body)',
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>

        {/* Inline reply box */}
        {user && (
          <FeedCardReplyBox
            postId={post.id}
            onSuccess={() => {
              if (setPosts) {
                setPosts(prev => prev.map(p =>
                  p.id === post.id
                    ? { ...p, response_count: (p.response_count || 0) + 1 }
                    : p
                ));
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

const CLAIM_STATUS_CONFIG = {
  verified:   { color: 'var(--color-verified)', bg: 'var(--color-verified-bg)', border: 'var(--color-verified-border)', label: '✅ Verified' },
  invalidated:{ color: 'var(--color-invalidated)', bg: 'var(--color-invalidated-bg)', border: 'var(--color-invalidated-border)', label: '❌ Invalidated' },
  disputed:   { color: 'var(--color-disputed)', bg: 'var(--color-disputed-bg)', border: 'var(--color-disputed-border)', label: '❓ Disputed' },
  neutral:    { color: 'var(--color-text-muted)', bg: 'transparent', border: 'var(--color-border)', label: '○ Unproven' },
  unverified: { color: 'var(--color-text-muted)', bg: 'transparent', border: 'var(--color-border)', label: '○ Unproven' },
};

function ClaimsPanel({ claims, claimStatuses }) {
  const [open, setOpen] = useState(false);
  if (!claims || claims.length === 0) return null;

  const statusByIndex = {};
  if (claimStatuses) {
    for (const cs of claimStatuses) statusByIndex[cs.claim_index] = cs;
  }

  const ORDER = { invalidated: 0, disputed: 1, verified: 2, neutral: 3, unverified: 3 };
  const claimsWithStatus = claims.map((c, i) => ({
    ...c,
    index: i,
    statusInfo: statusByIndex[i] || null,
  })).sort((a, b) => {
    const sa = ORDER[a.statusInfo?.status] ?? 1;
    const sb = ORDER[b.statusInfo?.status] ?? 1;
    return sa - sb;
  });

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '7px 12px', background: 'var(--color-surface-hover)', border: 'none',
          cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)', textAlign: 'left',
          fontFamily: 'var(--font-body)', fontWeight: 500,
        }}
      >
        <span>AI-identified claims</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div style={{ background: 'var(--color-surface)', padding: '4px 0' }}>
          {claimsWithStatus.map((c, i) => {
            const sc = CLAIM_STATUS_CONFIG[c.statusInfo?.status];
            return (
              <div
                key={c.index}
                style={{
                  padding: '8px 14px',
                  borderBottom: i < claimsWithStatus.length - 1 ? '1px solid var(--color-border)' : 'none',
                  background: sc ? sc.bg : 'transparent',
                  borderLeft: sc ? `3px solid ${sc.color}` : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {sc && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: sc.color,
                      background: sc.color + '22', padding: '2px 7px',
                      borderRadius: 10, flexShrink: 0, marginTop: 1,
                      fontFamily: 'var(--font-body)',
                    }}>
                      {sc.label}
                    </span>
                  )}
                  <p style={{ fontSize: 12, color: 'var(--color-text-primary)', margin: 0, lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>{c.claim_text}</p>
                </div>
                {c.statusInfo?.strongest_rebuttal_synopsis && (
                  <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '4px 0 0 0', fontStyle: 'italic', lineHeight: 1.4, fontFamily: 'var(--font-body)' }}>
                    Counter: {c.statusInfo.strongest_rebuttal_synopsis}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const card = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-card)',
  padding: 24,
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-card)',
};
const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text-primary)',
  fontSize: 14, outline: 'none', display: 'block', boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
};
const submitBtn = {
  padding: '8px 20px',
  background: 'var(--color-brand)',
  color: '#fff',
  border: 'none', borderRadius: 6, cursor: 'pointer',
  fontSize: 14, fontWeight: 600,
  fontFamily: 'var(--font-body)',
  transition: 'background 0.15s ease',
};
